import React, { useState } from 'react';
import axios from 'axios';

const API = '/api';

interface ShipmentItem { name: string; quantity: number; image?: string | null; }
interface BuyerShipment {
  order_id: string;
  account_id: string;
  waybill: string;
  carrier_name: string | null;
  created_at: string | null;
  items: ShipmentItem[];
}

// Wspolny guzik "Przesylki" uzywany w Wiadomosciach (App.tsx) i Zgloszeniach (Issues.tsx).
// Klik -> pobiera przesylki kupujacego (po kontach firmy). 0 -> komunikat, 1 -> od razu otwiera,
// wiele -> modal wyboru z produktami. Wybor wola onOpenShipment(order_id, account_id).
export default function BuyerShipmentsButton({
  buyerLogin,
  variant = 'desktop',
  onOpenShipment,
}: {
  buyerLogin: string;
  variant?: 'desktop' | 'mobile';
  onOpenShipment: (orderId: string, accountId: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [modalShipments, setModalShipments] = useState<BuyerShipment[] | null>(null);

  const fmtDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('pl-PL'); } catch { return ''; }
  };

  const handleClick = async () => {
    if (!buyerLogin || loading) return;
    setMsg('');
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shipments/by-buyer?buyer_login=${encodeURIComponent(buyerLogin)}`);
      const list: BuyerShipment[] = res.data?.shipments || [];
      if (list.length === 0) {
        setMsg('Ten klient nie ma przesy\u0142ki');
      } else if (list.length === 1) {
        onOpenShipment(list[0].order_id, list[0].account_id);
      } else {
        setModalShipments(list);
      }
    } catch (e) {
      setMsg('B\u0142\u0105d pobierania przesy\u0142ek');
    }
    setLoading(false);
  };

  const btnStyle: React.CSSProperties =
    variant === 'mobile'
      ? { fontSize: 12, padding: '4px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(147,197,253,0.3)', color: '#fff', cursor: 'pointer', fontWeight: 500, marginLeft: 6 }
      : { fontSize: 11, padding: '1px 10px', borderRadius: 10, border: '1px solid #bfdbfe', background: '#eff6ff', color: '#1d4ed8', cursor: 'pointer' };

  return (
    <>
      <button onClick={handleClick} disabled={loading || !buyerLogin} style={btnStyle} title={"Sprawd\u017a przesy\u0142ki tego klienta"}>
        {loading ? '\u23f3' : (variant === 'mobile' ? '\ud83d\ude9a' : '\ud83d\ude9a Przesy\u0142ki')}
      </button>
      {msg && (
        <div
          onClick={() => setMsg('')}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 380, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', textAlign: 'center' }}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }}>{'\ud83d\ude9a'}</div>
            <div style={{ fontSize: 15, color: '#374151', fontWeight: 500, marginBottom: 16 }}>{msg}</div>
            <button
              onClick={() => setMsg('')}
              style={{ fontSize: 13, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', cursor: 'pointer', fontWeight: 500 }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {modalShipments && (
        <div
          onClick={() => setModalShipments(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 20, maxWidth: 560, width: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{"Wybierz przesy\u0142k\u0119"}</div>
              <button onClick={() => setModalShipments(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>{'\u2715'}</button>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>
              {"Klient "}{buyerLogin}{" ma "}{modalShipments.length}{" przesy\u0142ek. Wybierz, kt\u00f3r\u0105 otworzy\u0107:"}
            </div>
            {modalShipments.map((s, si) => (
              <div
                key={s.order_id || si}
                onClick={() => { onOpenShipment(s.order_id, s.account_id); setModalShipments(null); }}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', transition: 'background 0.1s' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }}>{s.waybill || '\u2014'}</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{fmtDate(s.created_at)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>
                  {(s.items || []).length > 0
                    ? s.items.map((it, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          {it.image
                            ? <img src={it.image} alt='' style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid #f3f4f6' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
                            : <div style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>{'\ud83d\ude9a'}</div>}
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                            {it.quantity}{" \u00d7 "}{it.name || "\u2014"}
                          </span>
                        </div>
                      ))
                    : <span style={{ color: '#9ca3af' }}>{"Brak pozycji"}</span>}
                </div>
                {s.carrier_name && (
                  <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#eff6ff', color: '#1d4ed8' }}>
                    {s.carrier_name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
